<template>
    <v-row>
        <v-col cols="12" class="title-label" v-if="!noTitle">
            {{ $t('entrance.basic_info') }}
        </v-col>
        <v-col cols="12">
            <kimsa-text-field
                :label="$t('entrance.your_name')"
                :placeholder="$t('entrance.your_name_placeholder')"
                :rules="rules.name"
                @change="name = $event"
                :value="name"
            />
        </v-col>
        <v-col cols="12">
            <kimsa-combobox
                :label="$t('entrance.country')"
                :placeholder="$t('entrance.country_placeholder')"
                :items="countryOptions"
                item-text="label"
                icon="mdi-web"
                with-selected-dot
                @change="country = $event"
                :value="country"
                ref="country"
            />
        </v-col>
        <v-col cols="12">
            <kimsa-phone-field
                :label="$t('entrance.phone')"
                :placeholder="$t('entrance.phone_placeholder')"
                :value="phone"
                :code="phoneCode"
                @change="phone = $event"
                @change:code="onSelectPhoneCode"
            />
        </v-col>
        <v-col cols="12">
            <kimsa-date-picker
                :label="$t('entrance.birthDate')"
                :placeholder="$t('entrance.birthDate_placeholder')"
                solo flat
                force-value
                birth-date-mode
                :value="birthDate"
                @change="birthDate = $event"
                min="1900-01-01"
                max="2020-12-31"
                color="secondary"
            />
        </v-col>
        <v-col cols="12">
            <span class="label">{{ $t('entrance.gender') }}</span>
            <div class="segment-btns">
                <button
                    v-for="opt in genderOptions"
                    :key="opt.value"
                    type="button"
                    :class="['segment-btn', { 'segment-btn--active': gender === opt.value }]"
                    @click="gender = opt.value"
                >
                    {{ opt.name }}
                </button>
            </div>
        </v-col>
        <v-col cols="12" v-if="withFile">
            <kimsa-upload-file
                :label="$t('common.logo')"
                relative-path="images/routines"
                mime-types="image/*"
                @change="onSelectImage"
                :preview-img="previewImg"
                ref="img"
                height="150"
                @delete="onDeleteImg" />
        </v-col>
    </v-row>
</template>

<script>

import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import KimsaPhoneField from "@/components/common/inputs/KimsaPhoneField";

import {Timestamp} from '@/utils/Firebase'
import {mapActions, mapState} from 'vuex'
import {debounce} from "@/utils/Common";

export default {
    name: "BasicInfoStepper",
    components: {
        KimsaDatePicker,
        KimsaTextField,
        KimsaCombobox,
        KimsaUploadFile,
        KimsaPhoneField,
    },
    props: {
        stepperMode: {
            type: Boolean,
            default: false
        },
        noTitle: {
            type: Boolean,
            default: false
        },
        withFile: {
            type: Boolean,
            default: false
        },
    },
    data(vm) {
        return {
            name: '',
            phone: '',
            phoneCode: '',
            phoneCodeTouched: false,
            birthDate: '',
            gender: '',
            country: '',
            file: null,
            deletedImage: false,

            genderOptions: [
                {name: vm.$t('entrance.male'), value: 'M'},
                {name: vm.$t('entrance.female'), value: 'F'}
            ]
        }
    },
    computed: {
        ...mapState(['countries']),
        countryOptions(vm = this) {
            return vm.countries.map(country => ({ ...country, id: country.id, label: vm.countryLabel(country.name) }))
        },
        countryPayload(vm = this) {
            const country = vm.country
            if (!country || !country.id) return ''
            return { id: country.id, name: country.name || '', phoneCode: country.phoneCode || '' }
        },
        previewImg(vm = this) {
            if (vm.file?.img) return vm.file?.img
            if (vm.deletedImage) return null
            return vm.account?.logo?.url || null
        },
        form(vm = this) {
            return {
                name: vm.name,
                phone: vm.phone,
                phoneCode: vm.phoneCode,
                birthDate: vm.birthDate ? Timestamp.fromMillis(vm.$moment(vm.birthDate, 'YYYY-MM-DD').unix() * 1000) : '',
                gender: vm.gender,
                country: vm.countryPayload,
                file: vm.file,
                deletedImage: vm.deletedImage
            }
        },
        rules(vm = this) {
            return {
                name: [
                    v => !!v || vm.$t('rules.required'),
                    v => v && v.length >= 3 || vm.$t('rules.min_length', {num: '3'})
                ],
                phone: [
                    v => !!v || vm.$t('rules.required'),
                    v => v && v.length >= 8 || vm.$t('rules.min_length', {num: '8'})
                ]
            }
        }
    },
    async mounted() {
        const vm = this
        if (vm.countries.length === 0) await vm.bindCountries()
        vm.loadAccount()
    },
    methods: {
        ...mapActions(['bindCountries']),
        onSelectPhoneCode(code) {
            const vm = this
            vm.phoneCode = code || ''
            vm.phoneCodeTouched = true
        },
        onSelectImage(file) {
            const vm = this;
            if (!file) return;
            vm.deletedImage = false
            vm.file = file
        },
        onDeleteImg() {
            const vm = this
            vm.deletedImage = true
            vm.resetImg()
        },
        async uploadImg() {
            const vm = this;
            return await vm.$refs.img.upload()
        },
        resetImg() {
            const vm = this
            vm.file = null
            if(vm.$refs.img) vm.$refs.img.reset()
        },
        loadAccount() {
            const vm = this
            vm.name = vm.account?.name || ''
            vm.phone = vm.account?.phone || ''
            const rawBirthDate = vm.account?.birthDate
            if (rawBirthDate) {
                const birthDate = vm.$moment(rawBirthDate.seconds != null ? rawBirthDate.seconds * 1000 : rawBirthDate)
                if (birthDate.isValid()) vm.birthDate = birthDate.format('YYYY-MM-DD')
            }
            vm.gender = vm.account?.gender || ''
            vm.country = vm.countryOptions.find(country => country.name === vm.account?.country?.name) || ''
            if (vm.$refs.country) vm.$refs.country.model = vm.country
            if (vm.account?.phoneCode) {
                vm.phoneCode = vm.account.phoneCode
                vm.phoneCodeTouched = true
            } else {
                vm.phoneCode = vm.account?.country?.phoneCode || ''
            }
        },
        debouncedEmit: debounce(
            function () {
                this.$emit('change', this.form)
            }, 500),
        emitNow() {
            this.$emit('change', this.form)
        },
    },
    watch: {
        account() {
            if (!this.stepperMode) this.loadAccount()
        },
        country(val) {
            if (!this.phoneCodeTouched) this.phoneCode = val?.phoneCode || ''
        },
        form() {
            this.debouncedEmit()
        },
    },
}
</script>

<style scoped>

.title-label {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.02em;
    padding-bottom: 16px;
}

.label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.segment-btns {
    display: flex;
    gap: 8px;
}

.segment-btn {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    color: inherit;
    font-size: 14px;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    outline: none;
    transition: border-color .15s ease, background-color .15s ease, color .15s ease;
}

.segment-btn--active {
    background: rgba(102, 204, 204, 0.12);
    border-color: var(--v-secondary-base);
    color: var(--v-accent-base);
    font-weight: 600;
}

</style>